/**
 * Unified Car Status Management
 *
 * This file provides a single source of truth for car status handling across the application.
 * Database uses underscores (in_progress), we normalize and provide consistent utilities.
 */

// Database status values (as stored in Supabase)
export const CAR_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CLOSED: 'closed',
} as const;

export type CarStatus = typeof CAR_STATUS[keyof typeof CAR_STATUS];

// Status metadata for UI rendering
export interface StatusConfig {
  labelKey: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  color: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export const STATUS_CONFIG: Record<CarStatus, StatusConfig> = {
  [CAR_STATUS.PENDING]: {
    labelKey: 'status.pending',
    variant: 'secondary',
    color: 'muted',
    bgClass: 'bg-muted',
    textClass: 'text-muted-foreground',
    borderClass: 'border-border',
  },
  [CAR_STATUS.IN_PROGRESS]: {
    labelKey: 'status.inProgress',
    variant: 'default',
    color: 'blue',
    bgClass: 'bg-blue-500/10',
    textClass: 'text-blue-600 dark:text-blue-400',
    borderClass: 'border-blue-500/20',
  },
  [CAR_STATUS.COMPLETED]: {
    labelKey: 'status.completed',
    variant: 'outline',
    color: 'green',
    bgClass: 'bg-green-500/10',
    textClass: 'text-green-600 dark:text-green-400',
    borderClass: 'border-green-500/20',
  },
  [CAR_STATUS.CLOSED]: {
    labelKey: 'status.closed',
    variant: 'outline',
    color: 'gray',
    bgClass: 'bg-gray-500/10',
    textClass: 'text-gray-600 dark:text-gray-400',
    borderClass: 'border-gray-500/20',
  },
};

/**
 * Get status configuration for a given status value
 */
export function getStatusConfig(status: string): StatusConfig {
  const normalized = status.toLowerCase().trim();
  return STATUS_CONFIG[normalized as CarStatus] || STATUS_CONFIG[CAR_STATUS.PENDING];
}

/**
 * Get translation key for a status label
 */
export function getStatusLabel(status: string): string {
  return getStatusConfig(status).labelKey;
}

/**
 * Check if a status is valid
 */
export function isValidStatus(status: string): status is CarStatus {
  return Object.values(CAR_STATUS).includes(status as CarStatus);
}

/**
 * Get all available statuses
 */
export function getAllStatuses(): CarStatus[] {
  return Object.values(CAR_STATUS);
}

/**
 * Status transition helpers
 */
export function canClaimCar(status: string): boolean {
  const normalized = status as CarStatus;
  return normalized === CAR_STATUS.PENDING || normalized === CAR_STATUS.IN_PROGRESS;
}

export function canCompleteCar(status: string): boolean {
  const normalized = status as CarStatus;
  return normalized === CAR_STATUS.IN_PROGRESS;
}

export function canReopenCar(status: string): boolean {
  const normalized = status as CarStatus;
  return normalized === CAR_STATUS.COMPLETED || normalized === CAR_STATUS.CLOSED;
}
